import * as XLSX from 'xlsx';
import { Car, CarStatus } from '../types';

// Standard template keys and human-friendly headers
export interface ExcelCarRow {
  'Make': string;
  'Model': string;
  'Year': number;
  'Price ($)': number;
  'Mileage (mi)': number;
  'Color': string;
  'VIN': string;
  'Description': string;
  'Features (comma-separated)': string;
  'Images (comma-separated)': string;
  'Status (Available/Pending/Sold)': string;
  'Featured (yes/no)': string;
  'Category (SUV/Sedan/Truck/Coupe/Luxury)': string;
}

// Fallback images array if the user doesn't specify images in Excel
const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?q=80&w=800',
  'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=800'
];

/**
 * Normalizes string keys and maps them using intelligent fuzzy logic
 */
const fuzzyMatchHeader = (header: string): keyof Omit<Car, 'id'> | null => {
  const clean = header.toLowerCase().trim();

  if (clean.includes('make') || clean.includes('manufacturer') || clean.includes('brand')) return 'make';
  if (clean.includes('model') || clean.includes('trim')) return 'model';
  if (clean.includes('year') || clean.includes('yr')) return 'year';
  if (clean.includes('price') || clean.includes('cost') || clean.includes('msrp') || clean.includes('val')) return 'price';
  if (clean.includes('mile') || clean.includes('odo') || clean.includes('km') || clean.includes('run')) return 'mileage';
  if (clean.includes('color') || clean.includes('colour') || clean.includes('paint')) return 'color';
  if (clean.includes('vin') || clean.includes('serial')) return 'vin';
  if (clean.includes('desc') || clean.includes('about') || clean.includes('detail')) return 'description';
  if (clean.includes('feat') || clean.includes('spec') || clean.includes('opt')) return 'features';
  if (clean.includes('image') || clean.includes('pic') || clean.includes('photo') || clean.includes('url')) return 'images';
  if (clean.includes('status') || clean.includes('avail') || clean.includes('sale')) return 'status';
  if (clean.includes('feature') || clean.includes('promo') || clean.includes('spot')) return 'featured';
  if (clean.includes('category') || clean.includes('type') || clean.includes('class') || clean.includes('body')) return 'category';

  return null;
};

export const excelService = {
  /**
   * Generates a beautifully formatted template spreadsheet
   * and triggers a direct browser download.
   */
  downloadTemplate: () => {
    const templateData = [
      {
        'Make': 'Nebula',
        'Model': 'Interceptor 5',
        'Year': 2021,
        'Price ($)': 68500,
        'Mileage (mi)': 4200,
        'Color': 'Obsidian Void',
        'VIN': 'NB21INT55X0001',
        'Description': 'Zero-emissions luxury performance. Features the patented Gravity-Drive suspension system for a cloud-like ride.',
        'Features (comma-separated)': 'Neuro-Link HUD, AI Copilot Nia, Solar-Reactive Paint, Magnetic Brakes',
        'Images (comma-separated)': 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=800, https://images.unsplash.com/photo-1494976388531-d1058494cdd8?q=80&w=800',
        'Status (Available/Pending/Sold)': 'Available',
        'Featured (yes/no)': 'yes',
        'Category (SUV/Sedan/Truck/Coupe/Luxury)': 'Luxury'
      },
      {
        'Make': 'Zorya',
        'Model': 'Horizon S',
        'Year': 2019,
        'Price ($)': 42000,
        'Mileage (mi)': 18500,
        'Color': 'Stellar Copper',
        'VIN': 'ZY19HORZ99S0002',
        'Description': 'The ultimate adventure SUV. Lightweight titanium alloy frame with multi-terrain sensory adaptation.',
        'Features (comma-separated)': '360 Thermal View, Self-Cleaning Glass, Bio-Metric Locks, Hover-Assist Ready',
        'Images (comma-separated)': 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?q=80&w=800',
        'Status (Available/Pending/Sold)': 'Available',
        'Featured (yes/no)': 'no',
        'Category (SUV/Sedan/Truck/Coupe/Luxury)': 'SUV'
      }
    ];

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(templateData);

    // Set column widths to make it highly legible and professional inside Excel
    const columnWidths = [
      { wch: 15 }, // Make
      { wch: 18 }, // Model
      { wch: 10 }, // Year
      { wch: 12 }, // Price
      { wch: 14 }, // Mileage
      { wch: 15 }, // Color
      { wch: 20 }, // VIN
      { wch: 35 }, // Description
      { wch: 30 }, // Features
      { wch: 30 }, // Images
      { wch: 15 }, // Status
      { wch: 12 }, // Featured
      { wch: 15 }  // Category
    ];
    worksheet['!cols'] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dakar Template');

    // Trigger download
    XLSX.writeFile(workbook, 'dakar_inventory_template.xlsx');
  },

  /**
   * Exports an array of Car objects into a fully formatted, downloadable Excel sheet.
   */
  exportToExcel: (cars: Car[]) => {
    const dataToExport = cars.map(car => ({
      'ID (Do Not Modify for Updates)': car.id,
      'Make': car.make,
      'Model': car.model,
      'Year': car.year,
      'Price ($)': car.price,
      'Mileage (mi)': car.mileage,
      'Color': car.color,
      'VIN': car.vin,
      'Description': car.description,
      'Features (comma-separated)': (car.features || []).join(', '),
      'Images (comma-separated)': (car.images || []).join(', '),
      'Status (Available/Pending/Sold)': car.status,
      'Featured (yes/no)': car.featured ? 'yes' : 'no',
      'Category (SUV/Sedan/Truck/Coupe/Luxury)': car.category
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    // Style column widths
    worksheet['!cols'] = [
      { wch: 25 }, // ID
      { wch: 15 }, // Make
      { wch: 18 }, // Model
      { wch: 10 }, // Year
      { wch: 12 }, // Price
      { wch: 14 }, // Mileage
      { wch: 15 }, // Color
      { wch: 20 }, // VIN
      { wch: 35 }, // Description
      { wch: 30 }, // Features
      { wch: 30 }, // Images
      { wch: 15 }, // Status
      { wch: 12 }, // Featured
      { wch: 15 }  // Category
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Dakar Inventory');
    XLSX.writeFile(workbook, `dakar_inventory_export_${new Date().toISOString().split('T')[0]}.xlsx`);
  },

  /**
   * Reads an uploaded Excel/CSV file, intelligently matches headers, and returns validated rows.
   */
  parseExcelFile: (file: File): Promise<{ success: boolean; data: Omit<Car, 'id'>[]; errors: string[] }> => {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Get matrix format to easily extract custom headers and perform validation
          const matrix = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });
          if (matrix.length === 0) {
            resolve({ success: false, data: [], errors: ['Spradsheet is empty or corrupt.'] });
            return;
          }

          const rawHeaders = matrix[0] as string[];
          const bodyRows = matrix.slice(1);
          
          // Map index to field names of Omit<Car, 'id'>
          const headerMapping: { [colIndex: number]: keyof Omit<Car, 'id'> } = {};
          rawHeaders.forEach((header, idx) => {
            if (!header) return;
            const matchedField = fuzzyMatchHeader(header);
            if (matchedField) {
              headerMapping[idx] = matchedField;
            }
          });

          // Verify we have at least minimum critical headers (make, model, year, price)
          const matchedFieldsList = Object.values(headerMapping);
          const requiredFields: (keyof Omit<Car, 'id'>)[] = ['make', 'model', 'year', 'price'];
          const missingFields = requiredFields.filter(field => !matchedFieldsList.includes(field));

          if (missingFields.length > 0) {
            resolve({
              success: false,
              data: [],
              errors: [`Missing required spreadsheets columns: ${missingFields.map(f => f.toUpperCase()).join(', ')}.`]
            });
            return;
          }

          const parsedCars: Omit<Car, 'id'>[] = [];
          const rowErrors: string[] = [];

          bodyRows.forEach((row, rowIndex) => {
            // Skip purely empty rows
            if (!row || row.length === 0 || (row.length === 1 && !row[0])) return;

            const carItem: Partial<Car> = {
              make: '',
              model: '',
              year: new Date().getFullYear(),
              price: 0,
              mileage: 0,
              color: 'Various',
              vin: '',
              description: '',
              features: [],
              images: [],
              status: CarStatus.AVAILABLE,
              featured: false,
              category: 'Sedan'
            };

            // Map each column of the row based on the matched headers
            row.forEach((cellVal: any, colIdx: number) => {
              const field = headerMapping[colIdx];
              if (!field) return;

              if (cellVal === undefined || cellVal === null) return;

              if (field === 'year') {
                const yr = parseInt(cellVal, 10);
                carItem.year = isNaN(yr) ? new Date().getFullYear() : yr;
              } else if (field === 'price') {
                // Strips non-numerics like $, commas
                const priceStr = String(cellVal).replace(/[^0-9.]/g, '');
                const pr = parseFloat(priceStr);
                carItem.price = isNaN(pr) ? 0 : pr;
              } else if (field === 'mileage') {
                const milStr = String(cellVal).replace(/[^0-9.]/g, '');
                const mil = parseInt(milStr, 10);
                carItem.mileage = isNaN(mil) ? 0 : mil;
              } else if (field === 'features') {
                const list = String(cellVal)
                  .split(/[;,]/)
                  .map(f => f.trim())
                  .filter(Boolean);
                carItem.features = list;
              } else if (field === 'images') {
                const list = String(cellVal)
                  .split(/[;,]/)
                  .map(img => img.trim())
                  .filter(img => img.startsWith('http://') || img.startsWith('https://'));
                carItem.images = list;
              } else if (field === 'featured') {
                const val = String(cellVal).toLowerCase().trim();
                carItem.featured = val === 'yes' || val === 'true' || val === '1' || val === 'y';
              } else if (field === 'status') {
                const val = String(cellVal).trim().toLowerCase();
                if (val.includes('avail')) carItem.status = CarStatus.AVAILABLE;
                else if (val.includes('pend')) carItem.status = CarStatus.PENDING;
                else if (val.includes('sold')) carItem.status = CarStatus.SOLD;
                else carItem.status = CarStatus.AVAILABLE;
              } else if (field === 'category') {
                const val = String(cellVal).toUpperCase().trim();
                if (['SUV', 'SEDAN', 'TRUCK', 'COUPE', 'LUXURY'].includes(val)) {
                  // Proper case
                  carItem.category = (val.charAt(0) + val.slice(1).toLowerCase()) as any;
                } else {
                  carItem.category = 'Sedan';
                }
              } else {
                // simple string mappings (make, model, color, vin, description)
                (carItem as any)[field] = String(cellVal).trim();
              }
            });

            // Enforce basic image fallback if none loaded
            if (!carItem.images || carItem.images.length === 0) {
              carItem.images = [FALLBACK_IMAGES[rowIndex % FALLBACK_IMAGES.length]];
            }

            // Input Validation checks
            if (!carItem.make || !carItem.model) {
              rowErrors.push(`Row ${rowIndex + 2}: Missing manufacturer ("Make") or model name.`);
              return;
            }

            parsedCars.push(carItem as Omit<Car, 'id'>);
          });

          resolve({
            success: rowErrors.length === 0,
            data: parsedCars,
            errors: rowErrors
          });

        } catch (err: any) {
          resolve({ success: false, data: [], errors: [`Excel read error: ${err.message || 'unknown error'}`] });
        }
      };

      reader.onerror = () => {
        resolve({ success: false, data: [], errors: ['Failed to read attachment file.'] });
      };

      reader.readAsArrayBuffer(file);
    });
  }
};
